import React, { useState, useEffect } from 'react';
import { COLORS } from '../theme';

// The admin panel — users, roles, and the app registry.
//
// Its own module because it outgrew the shell. App.jsx was the whole client
// once; it now has Nav.jsx, AppView.jsx and hub/ beside it, and this component
// alone was 470 lines of the file. The button and table styles below move with
// it: every one of them is used here and nowhere else.

// ---- Button and table styles for the admin panel -------------------------
const headerBtn = {
  background: 'transparent',
  border: `1px solid ${COLORS.border}`,
  color: COLORS.textMuted,
  padding: '6px 14px',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
  transition: 'all 0.2s'
};

const primaryBtn = {
  background: COLORS.primary,
  border: 'none',
  color: '#fff',
  padding: '8px 16px',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 13,
  fontWeight: 600
};

// Hoisted on their third copy: the panel renders three tables with the same
// header row and the same row-level Remove button, and a fourth inline copy is
// how they drift apart.
const smallBtn = { ...headerBtn, padding: '4px 10px', fontSize: 12 };
const theadRowStyle = {
  textAlign: 'left',
  fontSize: 12,
  color: COLORS.textMuted,
  textTransform: 'uppercase'
};

// ---- Pure helpers, exported so they can be tested ------------------------
//
// Both of these decide something a reviewer had to reason about by reading the
// component, which is the definition of logic that wants a test.

/** "read, apps:write, " -> ['read', 'apps:write'] */
export function parsePermissions(raw) {
  return String(raw ?? '').split(',').map(p => p.trim()).filter(Boolean);
}

/**
 * Has the registry actually changed since the server handed it over?
 *
 * It gates whether save() issues PUT /api/admin/apps at all — so a wrong answer
 * makes an admin WITHOUT apps:write eat a 403 on a save that never touched the
 * registry. Compared as serialised JSON against the server's own copy, which
 * relies on key order surviving the spread in updateApp/addApp; asserting that
 * is most of why this is a named function rather than an inline expression.
 */
export function appsDirty(apps, snapshotJson) {
  return JSON.stringify(apps || []) !== snapshotJson;
}

// ---- Admin panel ---------------------------------------------------------

/**
 * @param initialConfig  TEST SEAM, and the only one. The panel fetches its own
 *   config, and `useEffect` does not run under server-side rendering — so a
 *   smoke test that merely imports this component renders "Loading…" and
 *   exercises none of it. That false green is worth naming: every style below
 *   is a bare identifier resolved at RENDER time, so renaming one passes the
 *   build and throws on /#/admin in production. Passing a config in renders the
 *   loaded body for real.
 */
export default function AdminPanel({ onBack, initialConfig = null }) {
  const [config, setConfig] = useState(initialConfig);
  const [loading, setLoading] = useState(!initialConfig);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('');
  const [newRoleName, setNewRoleName] = useState('');
  const [newAppId, setNewAppId] = useState('');
  // Raw text per role, parsed on blur — see commitPermissions.
  const [permDraft, setPermDraft] = useState({});
  // The registry as the server last gave it to us, serialised, so save() can
  // tell whether it was actually edited.
  const [initialApps, setInitialApps] = useState('[]');

  // ---- App registry edits ------------------------------------------------
  // The apps list is a table now, not a code constant, so this section is the
  // whole point: adding a tool is a row edit rather than a deploy.

  function updateApp(id, field, value) {
    setConfig(c => ({
      ...c,
      apps: c.apps.map(a => (a.id === id ? { ...a, [field]: value } : a))
    }));
  }

  function removeApp(id) {
    // Roles keep referencing a removed id, so re-adding it later inherits the
    // old grants. Say it here rather than letting it surprise someone.
    const granted = Object.entries(config.roles)
      .filter(([, r]) => !r.apps.includes('*') && r.apps.includes(id))
      .map(([name]) => name);
    if (granted.length && !window.confirm(
      `Remove "${id}"? These roles still list it and would grant it again if the id comes back: ${granted.join(', ')}`
    )) return;
    setConfig(c => ({ ...c, apps: c.apps.filter(a => a.id !== id) }));
  }

  function addApp() {
    const id = newAppId.trim().toLowerCase();
    if (!id) return;
    // The server's own pattern, sent with the payload rather than copied here:
    // a second definition drifts, and this one has to agree, because the id is
    // static text once the row exists — a row the server refuses can only be
    // removed and retyped, and it fails as a whole-form 400 after other edits.
    if (config.appIdPattern && !new RegExp(config.appIdPattern).test(id)) {
      setError(`"${id}" is not a valid app id — lower-case letters, digits and hyphens, 3-50 characters`);
      return;
    }
    if (config.apps.some(a => a.id === id)) {
      setError(`app "${id}" already exists`);
      return;
    }
    setConfig(c => ({
      ...c,
      apps: [
        ...c.apps,
        {
          id,
          name: id,
          description: '',
          url: '',
          healthEndpoint: '/api/health',
          icon: null,
          // New apps start as "coming soon": the server refuses a live card
          // with no URL, and that is the honest state until one is filled in.
          comingSoon: true,
          position: (Math.max(0, ...c.apps.map(a => a.position || 0)) + 10)
        }
      ]
    }));
    setNewAppId('');
    setError('');
  }

  // Two entry points, because there are now two kinds of caller. An admin gets
  // the whole config; someone holding only apps:write gets the registry alone.
  // Loading solely through the admin-gated config route is what made the new
  // permission unusable: the grantee saw "Failed to load admin config" and had
  // no path into the product at all.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        let data = null;
        const full = await fetch('/api/admin/config', { credentials: 'include' });
        if (full.ok) {
          data = await full.json();
        } else if (full.status === 403) {
          const appsOnly = await fetch('/api/admin/apps', { credentials: 'include' });
          if (!appsOnly.ok) throw new Error('no admin access');
          const payload = await appsOnly.json();
          data = { ...payload, roles: {}, users: {}, defaultRole: null, canEditRoles: false };
        } else {
          throw new Error('failed');
        }
        if (cancelled) return;
        // The list as loaded, so save() can tell whether the registry was
        // actually touched and skip a write the caller may not be allowed to
        // make. Comparing against the server's copy is the only honest test;
        // a dirty flag set by every keystroke marks a typed-then-undone edit.
        setInitialApps(JSON.stringify(data.apps || []));
        setConfig(data);
        if (data.defaultRole) setNewUserRole(data.defaultRole);
        setLoading(false);
      } catch {
        if (cancelled) return;
        setError('Failed to load admin config');
        setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  function updateUserRole(email, role) {
    setConfig(c => ({ ...c, users: { ...c.users, [email]: role } }));
  }

  function removeUser(email) {
    setConfig(c => {
      const users = { ...c.users };
      delete users[email];
      return { ...c, users };
    });
  }

  function addUser() {
    const email = newUserEmail.trim().toLowerCase();
    if (!email) return;
    if (config.users[email]) {
      setError(`${email} is already in the user list`);
      return;
    }
    setConfig(c => ({ ...c, users: { ...c.users, [email]: newUserRole } }));
    setNewUserEmail('');
    setError('');
  }

  // Permissions were stored and shown nowhere, so the only way to grant one was
  // a hand-written SQL update. 'apps:write' is the first that actually gates
  // something, so it has to be grantable here or it is not a real capability.
  //
  // The draft is held as RAW TEXT and parsed on blur. Deriving the input's
  // value from the parsed array instead — value={permissions.join(', ')} with
  // a split() on every keystroke — ate the separator as you typed it: "read,"
  // parsed to ['read'] and re-rendered as "read", so a second permission could
  // never be entered. The one capability this whole change introduces was
  // ungrantable through the only UI that grants it.
  function commitPermissions(roleName) {
    const raw = permDraft[roleName];
    if (raw == null) return;
    const permissions = parsePermissions(raw);
    setConfig(c => ({
      ...c,
      roles: { ...c.roles, [roleName]: { ...c.roles[roleName], permissions } }
    }));
    setPermDraft(d => {
      const next = { ...d };
      delete next[roleName];
      return next;
    });
  }

  function toggleAppForRole(roleName, appId) {
    setConfig(c => {
      const role = c.roles[roleName];
      // Wildcard roles ignore individual toggles.
      if (role.apps.includes('*')) return c;
      const has = role.apps.includes(appId);
      const apps = has ? role.apps.filter(a => a !== appId) : [...role.apps, appId];
      return { ...c, roles: { ...c.roles, [roleName]: { ...role, apps } } };
    });
  }

  function addRole() {
    const name = newRoleName.trim().toLowerCase();
    if (!name) return;
    if (config.roles[name]) {
      setError(`role "${name}" already exists`);
      return;
    }
    setConfig(c => ({
      ...c,
      roles: { ...c.roles, [name]: { apps: ['bstock-portal'], permissions: ['read'] } }
    }));
    setNewRoleName('');
    setError('');
  }

  function removeRole(name) {
    if (name === 'admin') {
      setError('Cannot delete the admin role');
      return;
    }
    if (Object.values(config.users).includes(name)) {
      setError(`Cannot delete role "${name}" — users are assigned to it`);
      return;
    }
    if (config.defaultRole === name) {
      setError(`Cannot delete role "${name}" — it is the default role`);
      return;
    }
    setConfig(c => {
      const roles = { ...c.roles };
      delete roles[name];
      return { ...c, roles };
    });
    setError('');
  }

  async function save() {
    setSaving(true);
    setError('');
    setSaveMsg('');
    try {
      // Two tables, two permissions, and therefore two writes — each attempted
      // only if this caller both changed it and may write it.
      //
      // The registry goes FIRST so a newly added id exists before any role is
      // written referencing it. That ordering does NOT prevent a dangling
      // reference on the way out: a removal deletes the row first, which is
      // why the server prunes roles.apps inside the same transaction rather
      // than relying on the client's ordering.
      //
      // These are separate requests, so a failure after the first leaves the
      // registry committed and the roles not. Say which half landed instead of
      // reporting a bare "Save failed" over a partly-applied change.
      const registryChanged = appsDirty(config.apps, initialApps);
      let appsCommitted = false;
      let revoked = [];

      if (registryChanged) {
        const appsRes = await fetch('/api/admin/apps', {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ apps: config.apps })
        });
        const appsData = await appsRes.json();
        if (!appsRes.ok) throw new Error(appsData.error || 'Save failed');
        appsCommitted = true;
        revoked = appsData.revokedFrom || [];
        setInitialApps(JSON.stringify(appsData.apps ?? config.apps));
      }

      if (config.canEditRoles) {
        const res = await fetch('/api/admin/config', {
          method: 'PUT',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            roles: config.roles,
            users: config.users,
            defaultRole: config.defaultRole
          })
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(
            appsCommitted
              ? `Apps saved, but roles and users did not: ${data.error || 'Save failed'}`
              : (data.error || 'Save failed')
          );
        }
      }

      // Report the access that was actually revoked, rather than warning about
      // it beforehand and never confirming what happened.
      setSaveMsg(
        revoked.length
          ? `Saved — access revoked: ${revoked.map(r => `${r.role} → ${r.app}`).join(', ')}`
          : 'Saved'
      );
      setTimeout(() => setSaveMsg(''), 2500);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <p style={{ textAlign: 'center', color: COLORS.textMuted, marginTop: 80 }}>Loading…</p>;
  }
  if (!config) {
    return <p style={{ textAlign: 'center', color: COLORS.danger, marginTop: 80 }}>{error}</p>;
  }

  const roleNames = Object.keys(config.roles);
  const sectionStyle = {
    background: COLORS.cardBg,
    border: `1px solid ${COLORS.border}`,
    borderRadius: 12,
    padding: 24,
    marginBottom: 24
  };
  const inputStyle = {
    background: COLORS.background,
    border: `1px solid ${COLORS.border}`,
    color: COLORS.text,
    padding: '6px 10px',
    borderRadius: 6,
    fontSize: 13
  };

  return (
    <main style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 700 }}>Admin</h2>
          <span style={{ fontSize: 13, color: COLORS.textMuted }}>
            {config.canEditRoles
              ? 'Manage apps, users, roles, and per-role app access'
              : 'Manage the apps shown on the launcher'}
          </span>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {saveMsg && <span style={{ color: COLORS.success, fontSize: 13 }}>{saveMsg}</span>}
          {error && <span style={{ color: COLORS.danger, fontSize: 13 }}>{error}</span>}
          <button onClick={onBack} style={headerBtn}>Back</button>
          <button onClick={save} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {/* Users — admins only. An apps:write holder edits the registry and
          nothing else, and two empty tables would read as "you have no users"
          rather than "this is not yours to edit". */}
      {config.canEditRoles && (
      <section style={sectionStyle}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>Users</h3>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16, alignItems: 'center' }}>
          <input
            type="email"
            placeholder="user@bstock.com"
            value={newUserEmail}
            onChange={e => setNewUserEmail(e.target.value)}
            style={{ ...inputStyle, flex: 1, maxWidth: 320 }}
          />
          <select
            value={newUserRole}
            onChange={e => setNewUserRole(e.target.value)}
            style={inputStyle}
          >
            {roleNames.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
          <button onClick={addUser} style={primaryBtn}>Add user</button>
        </div>

        <div style={{ fontSize: 13, color: COLORS.textMuted, marginBottom: 8 }}>
          Default role for anyone not listed below:{' '}
          <select
            value={config.defaultRole}
            onChange={e => setConfig(c => ({ ...c, defaultRole: e.target.value }))}
            style={inputStyle}
          >
            {roleNames.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
          <thead>
            <tr style={theadRowStyle}>
              <th style={{ padding: '8px 0' }}>Email</th>
              <th style={{ padding: '8px 0' }}>Role</th>
              <th style={{ padding: '8px 0', width: 80 }}></th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(config.users).sort(([a], [b]) => a.localeCompare(b)).map(([email, role]) => (
              <tr key={email} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                <td style={{ padding: '10px 0', fontSize: 14 }}>{email}</td>
                <td style={{ padding: '10px 0' }}>
                  <select
                    value={role}
                    onChange={e => updateUserRole(email, e.target.value)}
                    style={inputStyle}
                  >
                    {roleNames.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td style={{ padding: '10px 0', textAlign: 'right' }}>
                  <button
                    onClick={() => removeUser(email)}
                    style={smallBtn}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      )}

      {/* App registry */}
      <section style={sectionStyle}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Apps</h3>
        <p style={{ fontSize: 13, color: COLORS.textMuted, marginTop: 0, marginBottom: 16 }}>
          The launcher reads this table, so adding a tool is a row edit rather than a deploy.
          An app&rsquo;s id is what roles grant access to — it cannot be changed once created.
        </p>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            type="text"
            placeholder="new app id (e.g. image-library)"
            value={newAppId}
            onChange={e => setNewAppId(e.target.value)}
            style={{ ...inputStyle, flex: 1, maxWidth: 320 }}
          />
          <button onClick={addApp} style={primaryBtn}>Add app</button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={theadRowStyle}>
                <th style={{ padding: '8px 8px 8px 0' }}>Id</th>
                <th style={{ padding: '8px' }}>Name</th>
                <th style={{ padding: '8px' }}>URL</th>
                <th style={{ padding: '8px', textAlign: 'center' }}>Coming soon</th>
                <th style={{ padding: '8px', width: 70 }}>Order</th>
                <th style={{ padding: '8px', width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {config.apps.map(a => (
                <tr key={a.id} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: '10px 8px 10px 0', fontWeight: 600 }}>{a.id}</td>
                  <td style={{ padding: '10px 8px' }}>
                    <input
                      type="text"
                      value={a.name}
                      onChange={e => updateApp(a.id, 'name', e.target.value)}
                      style={{ ...inputStyle, width: '100%', minWidth: 140 }}
                    />
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <input
                      type="url"
                      placeholder="https://…"
                      value={a.url}
                      onChange={e => updateApp(a.id, 'url', e.target.value)}
                      style={{ ...inputStyle, width: '100%', minWidth: 220 }}
                    />
                  </td>
                  <td style={{ padding: '10px 8px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={Boolean(a.comingSoon)}
                      onChange={e => updateApp(a.id, 'comingSoon', e.target.checked)}
                    />
                  </td>
                  <td style={{ padding: '10px 8px' }}>
                    <input
                      type="number"
                      value={a.position ?? 100}
                      onChange={e => updateApp(a.id, 'position', parseInt(e.target.value, 10) || 0)}
                      style={{ ...inputStyle, width: 60 }}
                    />
                  </td>
                  <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                    <button
                      onClick={() => removeApp(a.id)}
                      style={smallBtn}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Role → apps matrix — admins only, same reason as Users above. */}
      {config.canEditRoles && (
      <section style={sectionStyle}>
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16 }}>App access by role</h3>

        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            type="text"
            placeholder="new role name (e.g. contractor)"
            value={newRoleName}
            onChange={e => setNewRoleName(e.target.value)}
            style={{ ...inputStyle, flex: 1, maxWidth: 320 }}
          />
          <button onClick={addRole} style={primaryBtn}>Add role</button>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={theadRowStyle}>
                <th style={{ padding: '8px 8px 8px 0' }}>Role</th>
                {config.apps.map(a => (
                  <th key={a.id} style={{ padding: '8px', textAlign: 'center' }}>{a.name}</th>
                ))}
                <th style={{ padding: '8px', minWidth: 180 }}>Permissions</th>
                <th style={{ padding: '8px', width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {roleNames.map(name => {
                const role = config.roles[name];
                const isWildcard = role.apps.includes('*');
                return (
                  <tr key={name} style={{ borderTop: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: '10px 8px 10px 0', fontWeight: 600 }}>
                      {name}
                      {isWildcard && (
                        <span style={{ marginLeft: 6, fontSize: 11, color: COLORS.accent }}>
                          (all apps)
                        </span>
                      )}
                    </td>
                    {config.apps.map(a => (
                      <td key={a.id} style={{ padding: '10px 8px', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          disabled={isWildcard}
                          checked={isWildcard || role.apps.includes(a.id)}
                          onChange={() => toggleAppForRole(name, a.id)}
                        />
                      </td>
                    ))}
                    <td style={{ padding: '10px 8px' }}>
                      <input
                        type="text"
                        placeholder="read, apps:write"
                        value={permDraft[name] ?? (role.permissions || []).join(', ')}
                        onChange={e => setPermDraft(d => ({ ...d, [name]: e.target.value }))}
                        onBlur={() => commitPermissions(name)}
                        style={{ ...inputStyle, width: '100%', minWidth: 160 }}
                      />
                    </td>
                    <td style={{ padding: '10px 8px', textAlign: 'right' }}>
                      {name !== 'admin' && (
                        <button
                          onClick={() => removeRole(name)}
                          style={smallBtn}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
      )}
    </main>
  );
}
